/**
 * Molds — API client.
 * Mirrors src/lib/supabase/molds.ts function signatures exactly.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';
import type { Mold, PMPlan } from '@/types/maintenance';

export type { CreateMoldInput, UpdateMoldInput } from '@/lib/supabase/molds';
export { computePMDueStatus } from '@/lib/supabase/molds';

// ─── Queries ──────────────────────────────────────────────────────────────────

export async function fetchMolds(_orgId: string | null): Promise<Mold[]> {
  return apiGet<Mold[]>('/api/molds');
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export async function fetchMoldsPaginated(
  _orgId: string | null,
  opts: { page?: number; pageSize?: number } = {},
): Promise<PaginatedResult<Mold>> {
  const params = new URLSearchParams();
  params.set('page', String(opts.page ?? 1));
  params.set('pageSize', String(opts.pageSize ?? 25));
  return apiGet<PaginatedResult<Mold>>(`/api/molds?${params.toString()}`);
}

export async function fetchMold(id: string): Promise<Mold | null> {
  try {
    return await apiGet<Mold>(`/api/molds/${id}`);
  } catch {
    return null;
  }
}

export async function fetchPMPlans(_orgId: string | null): Promise<PMPlan[]> {
  return apiGet<PMPlan[]>('/api/pm-plans');
}

export async function fetchMoldPMPlan(moldId: string): Promise<PMPlan | null> {
  try {
    return await apiGet<PMPlan>(`/api/molds/${moldId}/pm-plan`);
  } catch {
    return null;
  }
}

// ─── Mutations ────────────────────────────────────────────────────────────────

import type { CreateMoldInput as _CreateMoldInput } from '@/lib/supabase/molds';

export async function createMoldInDB(input: _CreateMoldInput, _organizationId: string): Promise<void> {
  await apiPost<void>('/api/molds', input);
}

import type { UpdateMoldInput as _UpdateMoldInput } from '@/lib/supabase/molds';

export async function updateMoldInDB(id: string, input: _UpdateMoldInput): Promise<void> {
  await apiPatch<void>(`/api/molds/${id}`, input);
}

export async function updateMoldShots(id: string, currentShots: number): Promise<void> {
  await apiPatch<void>(`/api/molds/${id}/shots`, { current_shots: currentShots });
}

export async function updateMoldStatus(id: string, status: Mold['status']): Promise<void> {
  await apiPatch<void>(`/api/molds/${id}/status`, { status });
}

export async function linkPartToMoldInDB(moldId: string, partId: string, notes?: string): Promise<void> {
  await apiPost<void>(`/api/molds/${moldId}/parts`, { part_id: partId, notes });
}

export async function unlinkPartFromMold(moldId: string, partId: string): Promise<void> {
  await apiDelete(`/api/molds/${moldId}/parts/${partId}`);
}

// ─── PM Plans ─────────────────────────────────────────────────────────────────

export interface CreatePMPlanInput {
  mold_id: string;
  trigger_type: 'shots' | 'calendar' | 'both';
  shot_interval?: number;
  calendar_interval_days?: number;
}

export interface PatchPMPlanInput {
  trigger_type?: 'shots' | 'calendar' | 'both';
  shot_interval?: number | null;
  calendar_interval_days?: number | null;
}

export async function createPMPlan(input: CreatePMPlanInput): Promise<PMPlan> {
  return apiPost<PMPlan>('/api/pm-plans', input);
}

export async function updatePMPlan(planId: string, input: PatchPMPlanInput): Promise<PMPlan> {
  return apiPatch<PMPlan>(`/api/pm-plans/${planId}`, input);
}
